"use client";

import { useId, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { Check } from "lucide-react";
import { identifyPosthogByEmail } from "@/lib/posthog-identify-email";

/**
 * The three success shapes `POST /api/newsletter` can answer with. Only
 * "subscribed" means a welcome mail actually went out, so the confirmation copy
 * has to follow the outcome instead of promising a mail on every 2xx.
 */
type SubscribeOutcome = "subscribed" | "already_active" | "opted_out";

const SUCCESS_MESSAGE_KEY: Record<SubscribeOutcome, string> = {
	subscribed: "newsletterSuccess",
	already_active: "newsletterSuccessAlreadyActive",
	opted_out: "newsletterSuccessOptedOut",
};

interface CalculatorNewsletterBannerProps {
	channel: string;
	locale: string;
	/** Display-currency-formatted discount amount (e.g. "5 €", "2.050 HUF"). */
	discountLabel: string;
}

export function CalculatorNewsletterBanner({
	channel,
	locale,
	discountLabel,
}: CalculatorNewsletterBannerProps) {
	const t = useTranslations("peptideCalculator.banner");
	const [email, setEmail] = useState("");
	const [consent, setConsent] = useState(false);
	const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
	const [outcome, setOutcome] = useState<SubscribeOutcome>("subscribed");
	const [errorType, setErrorType] = useState<string | null>(null);
	const identifyDedupeRef = useRef<string | null>(null);

	const consentFieldId = useId();
	const privacyHref = `/${locale}/${channel}/privacy`;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setErrorType(null);

		const trimmed = email.trim();
		if (!trimmed) {
			setErrorType("newsletterInvalidEmail");
			return;
		}
		if (!consent) {
			setErrorType("newsletterConsentRequired");
			return;
		}

		setStatus("loading");
		try {
			const res = await fetch("/api/newsletter", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ email: trimmed, consent, locale, channel, source: "calculator_banner" }),
			});
			const data = (await res.json()) as { error?: string; status?: string };

			if (!res.ok) {
				setStatus("error");
				const err = data?.error;
				setErrorType(
					err === "consent_required"
						? "newsletterConsentRequired"
						: err === "invalid_email" || err === "email_required"
							? "newsletterInvalidEmail"
							: "newsletterError",
				);
				return;
			}

			identifyPosthogByEmail(
				trimmed,
				{ newsletter_signup: true, newsletter_source: "calculator_banner" },
				identifyDedupeRef,
			);
			// An unknown or missing status keeps the old confirmation copy, so a client
			// served from cache before this deploy never renders a blank message.
			setOutcome(
				data?.status === "already_active" || data?.status === "opted_out" ? data.status : "subscribed",
			);
			setStatus("success");
			setEmail("");
			setConsent(false);
		} catch {
			setStatus("error");
			setErrorType("newsletterError");
		}
	};

	return (
		<div className="overflow-hidden rounded-xl p-6 sm:p-8" style={{ background: "#1e3a5f" }}>
			{status === "success" ? (
				<div className="flex items-center justify-center gap-3 py-4">
					<div
						className="flex h-10 w-10 items-center justify-center rounded-full"
						style={{ background: "rgba(255,255,255,0.2)" }}
					>
						<Check className="h-5 w-5" style={{ color: "#fff" }} />
					</div>
					<p className="text-sm font-medium sm:text-base" style={{ color: "#fff" }}>
						{t(SUCCESS_MESSAGE_KEY[outcome])}
					</p>
				</div>
			) : (
				<>
					<p
						className="text-xs font-medium uppercase tracking-[0.2em]"
						style={{ color: "rgba(255,255,255,0.7)" }}
					>
						{t("newsletterEyebrow")}
					</p>
					<h2 className="mt-2 text-xl font-bold sm:text-2xl" style={{ color: "#fff" }}>
						{t("newsletterTitle", { amount: discountLabel })}
					</h2>
					<p className="mt-1 text-sm sm:text-base" style={{ color: "rgba(255,255,255,0.85)" }}>
						{t("newsletterSubtitle")}
					</p>

					<form onSubmit={handleSubmit} className="mt-5 space-y-3">
						<div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
							<input
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder={t("newsletterPlaceholder")}
								disabled={status === "loading"}
								className="h-10 flex-1 rounded-md px-4 text-sm focus:outline-none focus:ring-2 disabled:opacity-60 sm:h-11"
								style={{
									background: "rgba(255,255,255,0.12)",
									border: "1px solid rgba(255,255,255,0.25)",
									color: "#fff",
								}}
								aria-invalid={!!errorType}
								aria-describedby={errorType ? "calc-newsletter-error" : undefined}
							/>
							<button
								type="submit"
								disabled={status === "loading"}
								className="h-10 shrink-0 rounded-md bg-white px-6 text-sm font-semibold text-primary transition-colors hover:bg-white/90 disabled:opacity-60 sm:h-11"
							>
								{status === "loading" ? "…" : t("newsletterSubscribe")}
							</button>
						</div>

						{/* Avoid <a> inside <label>: browsers mishandle hit-testing; use htmlFor + id instead. */}
						<div className="flex items-start gap-2 text-left">
							<input
								id={consentFieldId}
								type="checkbox"
								checked={consent}
								onChange={(e) => setConsent(e.target.checked)}
								disabled={status === "loading"}
								className="relative z-10 mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded accent-white"
								style={{
									border: "1px solid rgba(255,255,255,0.3)",
									background: "rgba(255,255,255,0.1)",
								}}
							/>
							<div
								className="min-w-0 text-[11px] leading-relaxed"
								style={{ color: "rgba(255,255,255,0.75)" }}
							>
								<label htmlFor={consentFieldId} className="cursor-pointer">
									{t("newsletterConsent")}
								</label>
								<Link
									href={privacyHref}
									className="underline underline-offset-2"
									style={{ color: "rgba(255,255,255,0.9)" }}
								>
									{t("newsletterConsentPrivacyLink")}
								</Link>
								{t("newsletterConsentSuffix")}
							</div>
						</div>

						{errorType && (
							<p
								id="calc-newsletter-error"
								className="text-xs font-medium"
								role="alert"
								style={{ color: "rgba(255,255,255,0.95)" }}
							>
								{t(errorType)}
							</p>
						)}
					</form>
				</>
			)}
		</div>
	);
}
